import React, { useState, useEffect, useCallback } from 'react';

import { useAuth } from '../../../context/AuthContext';
import './OnboardingTour.css';

const OnboardingTour = () => {
    const { currentUser } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [positionStyle, setPositionStyle] = useState({});
    const [spotlightStyle, setSpotlightStyle] = useState({});
    const [tourSteps, setTourSteps] = useState([]);

    useEffect(() => {
        if (!currentUser) return;

        // Passos base
        let steps = [
            {
                target: '.logo-container',
                title: 'Bem-vindo ao MAGO!',
                content: 'Este é o seu sistema de Monitoramento e Avaliação da Gestão Organizacional.',
                position: 'bottom'
            },
            {
                target: '.desktop-nav-links',
                title: 'Menu Principal',
                content: 'Utilize este menu para navegar entre os painéis e funcionalidades.',
                position: 'bottom'
            }
        ];

        // Passos específicos por perfil
        if (currentUser.perfil === 'ARTICULADOR') {
            steps.push({
                target: 'a[href="/articulador/dashboard"]',
                title: 'Dashboard',
                content: 'Visualize o progresso das suas questões.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/articulador/painel-organizacao"]',
                title: 'Painel da Organização',
                content: 'Veja o status geral da organização.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/articulador/questoes"]',
                title: 'Minhas Questões',
                content: 'Responda e gerencie as questões atribuídas a você.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/articulador/exportar"]',
                title: 'Exportar Dados',
                content: 'Exporte seus dados para análise.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/meu-perfil"]',
                title: 'Meu Perfil',
                content: 'Gerencie seus dados pessoais e senha.',
                position: 'bottom'
            });

        } else if (currentUser.perfil === 'ADM.ORG') {
            steps.push({
                target: 'a[href="/adm-org/painel"]',
                title: 'Painel Geral',
                content: 'Visão geral do status da organização.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/adm-org/questoes"]',
                title: 'Questões',
                content: 'Gerencie as questões da organização.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/adm-org/priorizacao"]',
                title: 'Priorização Estratégica',
                content: 'Defina a importância de cada questão baseada na realidade da sua instituição.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/adm-org/atribuir-questoes"]',
                title: 'Distribuição de Tarefas',
                content: 'Atribua responsabilidades aos articuladores para descentralizar o preenchimento.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/adm-org/usuarios"]',
                title: 'Gestão de Usuários',
                content: 'Cadastre e gerencie articuladores e permissões de acesso.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/adm-org/exportar"]',
                title: 'Exportar Dados',
                content: 'Exporte os dados para análise externa.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/meu-perfil"]',
                title: 'Meu Perfil',
                content: 'Gerencie seus dados pessoais.',
                position: 'bottom'
            });

        } else if (currentUser.perfil === 'ADM MASTER') {
            steps.push({
                target: 'a[href="/admin/dashboard"]',
                title: 'Dashboard Global',
                content: 'Visão global de todas as organizações.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/admin/questoes"]',
                title: 'Banco de Questões',
                content: 'Gerencie o banco de questões do sistema.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/admin/usuarios"]',
                title: 'Gestão de Usuários',
                content: 'Gerencie todos os usuários do sistema.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/admin/ranking"]',
                title: 'Ranking',
                content: 'Visualize o ranking das organizações.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/admin/logs"]',
                title: 'Logs de Auditoria',
                content: 'Acompanhe as atividades e alterações realizadas no sistema.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/admin/exportar"]',
                title: 'Exportar',
                content: 'Ferramentas de exportação de dados em massa.',
                position: 'bottom'
            });
            steps.push({
                target: 'a[href="/meu-perfil"]',
                title: 'Meu Perfil',
                content: 'Gerencie seus dados pessoais.',
                position: 'bottom'
            });
        }

        // Adicionar Ajuda no final para todos
        steps.push({
            target: 'a[href="/ajuda"]',
            title: 'Central de Ajuda',
            content: 'Consulte manuais e documentos importantes a qualquer momento.',
            position: 'bottom'
        });

        setTourSteps(steps);

        const tourKey = `mago_tour_completed_${currentUser.uid}`;
        const tourCompleted = localStorage.getItem(tourKey);

        if (!tourCompleted) {
            setTimeout(() => setIsVisible(true), 1500);
        }
    }, [currentUser]);

    const handleComplete = useCallback(() => {
        setIsVisible(false);
        if (currentUser) {
            const tourKey = `mago_tour_completed_${currentUser.uid}`;
            localStorage.setItem(tourKey, 'true');
        }
    }, [currentUser]);

    useEffect(() => {

        if (!isVisible || tourSteps.length === 0) return;

        const updatePosition = () => {
            if (currentStep >= tourSteps.length) {
                handleComplete();
                return;
            }

            const step = tourSteps[currentStep];
            const element = document.querySelector(step.target);

            if (element) {
                const rect = element.getBoundingClientRect();

                // Estilo para o holofote (spotlight)
                setSpotlightStyle({
                    top: rect.top - 5,
                    left: rect.left - 5,
                    width: rect.width + 10,
                    height: rect.height + 10,
                    opacity: 1
                });

                // Estilo para o card
                const style = {
                    top: rect.bottom + 20,
                    left: rect.left
                };

                // Ajustes de posicionamento para não sair da tela
                if (window.innerWidth - rect.left < 340) {
                    style.left = Math.max(10, rect.right - 320);
                }

                // Se estiver muito embaixo na tela, mostra em cima do elemento
                if (window.innerHeight - rect.bottom < 250) {
                    style.top = rect.top - 280; // Aproximadamente altura do card
                }

                setPositionStyle(style);
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                // Fallback: Centralizado se elemento não encontrado
                setSpotlightStyle({ opacity: 0 });
                setPositionStyle({
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    position: 'fixed'
                });
            }
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [currentStep, isVisible, tourSteps, handleComplete]);

    const handleNext = () => {
        if (currentStep < tourSteps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleComplete();
        }
    };


    const handleSkip = () => {
        handleComplete();
    };

    if (!isVisible || !currentUser || tourSteps.length === 0) return null;

    const step = tourSteps[currentStep];

    return (
        <div className="tour-overlay">
            <div className="tour-spotlight" style={spotlightStyle}></div>
            <div className="tour-card" style={positionStyle}>
                <div className="tour-header">
                    <h3>{step?.title || 'Tutorial'}</h3>
                    <button className="tour-close-btn" onClick={handleSkip} title="Fechar tutorial">&times;</button>
                </div>
                <div className="tour-body">
                    <p>{step?.content || ''}</p>
                </div>
                <div className="tour-footer">
                    <span className="tour-progress">{currentStep + 1} / {tourSteps.length}</span>
                    <div className="tour-actions">
                        <button className="tour-btn-skip" onClick={handleSkip}>Pular</button>
                        <button className="tour-btn-next" onClick={handleNext}>
                            {currentStep === tourSteps.length - 1 ? 'Concluir' : 'Próximo'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingTour;
